import React, { useState, useEffect } from 'react';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { getOpenHoursText, getAddressText } from '@src/utills/utils';
import { formatCostField, capitalizeSentenceFirstLetters } from '@repo/utils';
import { Brandmark } from '@uhg-abyss/web/ui/Brandmark';
import { Box } from '@uhg-abyss/web/ui/Box';
import { ReferralFormModal } from '../ReferralForm/ReferralForm';
import { CboReferralSubmissionResults } from '../CboReferralSubmissionResults/CboReferralSubmissionResults';
import { loggedInAdvocateAtom, memberInContextAtom } from '@src/global-state';
import { useAtomValue } from 'jotai';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';
import { CboCardFindhelpNextSteps } from '@repo/ui';

export type DetailsPageProps = {
  programData: any;

  printTab: boolean;
};

export const DetailsPage = ({ programData, printTab }: DetailsPageProps) => {
  useEffect(() => {
    if (printTab) {
      window.print();
    }
  }, [printTab]);

  const cost = formatCostField(programData?.free_or_reduced);
  const featureFlags = getFeatureFlagsFromStorage();
  const addressText = getAddressText(programData);
  const memberInContext = useAtomValue(memberInContextAtom);
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  const [showReferralModal, setShowReferralModal] = useState<boolean>(false);
  const [referralResponse, setReferralResponse] = useState<
    any | null | undefined
  >(undefined);

  const NextStepsElement = () => {
    return (
      <CboCardFindhelpNextSteps
        nextStepsData={programData.next_steps}
        firstOfficeAddressText={addressText}
        firstOfficeEmail={programData.offices[0]?.email ?? ''}
        firstOfficePhone={programData.offices[0]?.phone_number ?? ''}
        firstOfficeWebsite={programData.offices[0]?.website_url ?? ''}
        source='advocate'
      />
    );
  };

  return (
    <React.Fragment>
      {/* Will display if the user clicks 'Log Referral' button */}
      {showReferralModal && (
        <ReferralFormModal
          vendor="findhelp"
          referringTo={programData}
          // FIXME - Type error
          setReferringTo={() => { }}
          setReferResponse={(resp: any) => {
            setReferralResponse(resp);
          }}
          isOpen={showReferralModal}
          setIsOpen={setShowReferralModal}
          memberInContext={memberInContext}
          paCoalitionConsent={
            featureFlags?.agent_pennsylvania_coalition_consent
          }
          loggedInAdvocate={loggedInAdvocate}
        />
      )}

      {
        /* Will display after the user attempts to submit a referral */
        referralResponse !== undefined && (
          <CboReferralSubmissionResults
            referResp={referralResponse}
            setReferResp={setReferralResponse}
          />
        )
      }

      <div
        className="printOnly"
        style={{ top: 0, position: 'absolute', width: '100%' }}
      >
        <Box color="$white">
          <Brandmark
            brand="uhc"
            size="$sm"
            affiliate="uhc"
            variant="lockup"
            color="blue"
          />
        </Box>
        <hr />
      </div>

      <div
        className="noPrint"
        style={{
          // backgroundColor: '#eef8fb',
          display: 'flex',
          padding: '2%',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <Heading>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                color: 'black',
              }}
            >
              {programData.name}
            </span>
          </Heading>
          <Text color="$core.color.neutral.100">by&nbsp;</Text>
          <Text color="$core.color.neutral.100" fontSize="md">
            {programData.provider_name}
          </Text>
          <br />
          <br />
          {/* <div>
              {programData.accepts_referrals ? (
                <Text
                  color="$core.color.neutral.100"
                  fontWeight="bold"
                  fontSize="md"
                  style={{
                    paddingLeft: '1px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IconSymbol icon="verified" size="24px" color="black" />
                  This organization receives member information
                </Text>
              ) : (
                <Text
                  color="Red"
                  fontWeight="bold"
                  size="$md"
                  style={{
                    paddingLeft: '1px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IconSymbol
                    icon="do_not_disturb_on"
                    size="24px"
                    color="black"
                  />
                  This organization does not receive member information
                </Text>
              )}
            </div> */}
          <br />
          <span
            style={{
              display: 'inline-block',
            }}
            dangerouslySetInnerHTML={{ __html: programData.description }}
          />
        </div>
      </div>

      <Heading
        color="$core.color.neutral.100"
        className="noPrint"
        style={{
          paddingRight: '2%',
          paddingLeft: '2%',
          paddingTop: '2%',
        }}
      >
        Information
      </Heading>
      <Heading className="printOnly" color="$core.color.neutral.100">
        {programData.name}
      </Heading>
      <br className="printOnly" />
      <p className="printOnly">
        <span>by </span>
        <Text color="$core.color.neutral.100">{programData.provider_name}</Text>
      </p>
      <br className="printOnly" />

      <Text className="printOnly">
        <div dangerouslySetInnerHTML={{ __html: programData.description }} />
      </Text>
      <br className="printOnly" />
      <div className="information">
        <div>
          <Text color="$core.color.neutral.100" fontWeight="bold" fontSize="md" width="100%">
            Main Services
          </Text>
          <br />
          {programData.service_tags.length > 0 ? (
            <span className="leftcolspacing">
              {programData.service_tags
                .map((tag) => {
                  return capitalizeSentenceFirstLetters(tag);
                })
                .join(', ')}
            </span>
          ) : (
            <span className="leftcolspacing">
              No main services information available
            </span>
          )}

          <br />
          <br className="noPrint" />
          <br className="printOnly" />
          <Text color="$core.color.neutral.100" fontWeight="bold" fontSize="md" width="100%">
            Serving
          </Text>
          <br />
          {programData.attribute_tags.length > 0 ? (
            <span className="leftcolspacing">
              {programData.attribute_tags
                .map((tag) => {
                  return capitalizeSentenceFirstLetters(tag);
                })
                .join(', ')}
            </span>
          ) : (
            <span className="leftcolspacing">
              No serving information available
            </span>
          )}

          <br />
          <br className="noPrint" />
          <br className="printOnly" />
          <Text color="black" fontWeight="bold" size="$md" width="100%">
            Eligibility
          </Text>
          <br />
          {programData.rules ? (
            <span className="leftcolspacing">{programData.rules}</span>
          ) : (
            <span className="leftcolspacing">
              No eligibility information is available
            </span>
          )}
          <br />
          <br className="noPrint" />
          <br className="printOnly" />
          <Text color="black" fontWeight="bold" size="$md" width="100%">
            Cost
          </Text>
          <br />
          <span className="leftcolspacing">{cost}</span>
          <br />
          <br className="noPrint" />
          <br className="printOnly" />
          <Text color="black" fontWeight="bold" size="$md" width="100%">
            Languages
          </Text>
          <br />
          {programData.supported_languages.length > 0 ? (
            <span className="leftcolspacing" style={{ lineHeight: 1.5 }}>
              {programData.supported_languages
                .map((language) => {
                  return capitalizeSentenceFirstLetters(language);
                })
                .join(', ')}
            </span>
          ) : (
            'No language information is available'
          )}

          <br />
          <br className="noPrint" />
          <br className="printOnly" />
          <Text color="black" fontWeight="bold" size="$md" width="100%">
            Availability
          </Text>
          <br />
          <span className="leftcolspacing">
            {programData.availability
              ? programData.availability.charAt(0).toUpperCase() + programData.availability.slice(1)
              : 'No availability information is available'}
          </span>
          <br />
          <br className="noPrint" />
          <br className="printOnly" />
          <Text color="black" fontWeight="bold" size="$md" width="100%">
            Coverage Area
          </Text>
          <br />
          <span className="leftcolspacing">
            {programData.coverage_description
              ? programData.coverage_description
              : 'No coverage area information is available'}
          </span>
          <br />
          <br className="noPrint" />
          <br className="printOnly" />
        </div>
        <div>
          <NextStepsElement />
          <br className="noPrint" />
          <br className="printOnly" />
          <Text color="black" fontWeight="bold" size="$md" width="100%">
            Location
          </Text>
          <br />
          <Text>{addressText}</Text>
          <br />
          <Text>
            {programData.distance} miles from {programData.zipcode}
          </Text>
          <br />
          <br className="noPrint" />
          <br className="printOnly" />
          <React.Fragment>
            <Text color="black" fontWeight="bold" size="$md" width="100%">
              Hours
            </Text>
            <br />
            <div
              data-cy="program-card-hours"
              style={{
                float: 'left',
                paddingTop: '5px',
                paddingBottom: '5px',
                width: '100%',
              }}
            >
              {(['sunday','monday','tuesday','wednesday','thursday','friday','saturday'] as const).map((day) => (
                <div key={day} style={{ lineHeight: 1.5 }}>
                  <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{day}:</span>{' '}
                  <span>{getOpenHoursText(programData.offices[0], day)}</span>
                </div>
              ))}
            </div>
          </React.Fragment>
        </div>
      </div>
      <footer className="printOnly">
        <hr />
        This resource was shared by United Healthcare Community Resources,
        powered by Findhelp.
      </footer>
    </React.Fragment>
  );
};
